/**
 * The one place this app talks to the server. Every screen goes through here.
 *
 * ── THE SESSION IS A COOKIE THIS CODE CANNOT READ ─────────────────────────
 * `withCredentials: true` on every request, and nothing here handles a token.
 * The session is httpOnly, so an injected script cannot steal it — which is
 * the reason it is not in `localStorage`. Unlike a Bearer-token setup there is
 * no header for the request interceptor to attach; the browser sends the
 * cookie on its own.
 */

import axios, { type AxiosError } from 'axios';
import { ApiError, ApiOffline, type ApiErrorBody } from './errors';
import { store } from '../store';
import { clearSelection } from '../store/workspaceSlice';

/**
 * Where the API is.
 *
 * Absolute, and not a Vite proxy: a proxy that silently fails is
 * indistinguishable from a broken API, and an absolute URL gives the better
 * failure — a stopped server throws a catchable network error, which the
 * screens render as "the API is not answering" with the command to start it.
 */
export const API_BASE: string =
	(import.meta.env?.VITE_API_URL as string | undefined) ??
	'http://localhost:4000/api';

const apiClient = axios.create({
	baseURL: API_BASE,
	withCredentials: true,
	headers: { 'Content-Type': 'application/json' },
});

/**
 * `Content-Type` only when a request actually carries a body — matching the
 * old fetch client's behaviour exactly. An axios instance's default header
 * applies unconditionally otherwise, which is a small but real difference
 * worth not introducing silently.
 */
apiClient.interceptors.request.use((config) => {
	if (!config.data) delete config.headers['Content-Type'];
	return config;
});

apiClient.interceptors.response.use(
	(response) => response,
	(error: AxiosError) => {
		if (!error.response)
			return Promise.reject(new ApiOffline(error.message));

		/* Session state itself lives in TanStack Query, not Redux (see
       `provider/auth.tsx`) — the redirect to /sign-in on a 401 already
       happens there, via `RequireSession`. This is the narrower, adjacent
       thing Redux DOES own: the selected engagement must not survive the
       person who selected it signing out, or the next person to sign in on
       this device would land looking at a stale selection. */
		if (error.response.status === 401) {
			store.dispatch(clearSelection());
		}

		const responseBody = error.response.data as
			{ error?: ApiErrorBody } | undefined;
		return Promise.reject(
			new ApiError(
				error.response.status,
				responseBody?.error ?? {
					code: 'SERVER_ERROR',
					message: `The API returned ${error.response.status}.`,
				},
			),
		);
	},
);

export default apiClient;
